import { tracking } from '@iwoca/frontend-tracking-library';

import { TMobileSignupForm } from './form';
import { useMobileSignupPersistence } from './persistence';
import {
  usePostApplicationMutation,
  usePostStateMutation,
} from '../../../api/lendingApi/lendingApiMutations';
import { useGetApplicationQuery } from '../../../api/lendingApi/lendingApiQueries';
import { getInitialApplicationBodyDE } from '../../../helpers/getApplicationBody';
import { getReferralSession } from '../../../helpers/referralSession';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useSignupParams } from '../../../hooks/useSignupParams';
import {
  getAmplitudeDefaultEventProperties,
  getAmplitudeDeviceCategory,
} from '../../../utils/amplitudeEvents';
import {
  DATA_LAYER_EVENT,
  postToDataLayer,
} from '../../../utils/googleDataLayer';
import { TGetStartedDataDE } from '../../GetStarted/GetStartedDE/GetStartedDE';
import { getInitialStateBody } from '../../GetStarted/GetStartedUK/GetStartedUK.helpers';
import { sanitiseAmount } from '../screens/AmountScreen/sanitiseAmount';

export const useMobileSignupAccountSetup = () => {
  const { affiliateTag, fromCampaign, referredAmount } = useSignupParams();
  const stateKey = useContextStateKey();
  const { application } = useGetApplicationQuery();
  const { putGetStartedStage } = useMobileSignupPersistence();
  const { mutatePostState } = usePostStateMutation();
  const { mutatePostApplication } = usePostApplicationMutation();

  const postApplication = async (
    targetStateKey: string,
    form: TMobileSignupForm,
  ) => {
    await mutatePostApplication({
      stateKey: targetStateKey,
      body: getInitialApplicationBodyDE(
        affiliateTag,
        {
          amount: form.amount,
          loanDuration: form.loanDuration || undefined,
          loanPurpose: form.loanPurpose || undefined,
          loanPurposeDetails: form.loanPurposeDetails,
        },
        getReferralSession(),
      ),
    });

    tracking.ampli.applicationStarted({
      requestedAmount: Number(form.amount),
      loanDuration: form.loanDuration || undefined,
      loanPurpose: form.loanPurpose || undefined,
      client_device: getAmplitudeDeviceCategory(),
      ...(referredAmount && {
        amount_query_matches:
          Number(form.amount) === sanitiseAmount(referredAmount),
      }),
      ...getAmplitudeDefaultEventProperties(),
    });

    postToDataLayer({
      event: DATA_LAYER_EVENT,
      applicationData: {
        applicantEmailAddress: form.email,
        requestedAmount: Number(form.amount),
        email_marketing_opt_in: form.marketingOptIn,
        lengthOfLoan: form.loanDuration,
        purpose: form.loanPurpose,
        detailed_purpose: form.loanPurposeDetails,
      },
    });
  };

  const setUpAccount = async (form: TMobileSignupForm) => {
    // Guard on stateKey not application: a prior attempt may have minted state but failed the application POST, and guarding on application alone would mint a second orphan state.
    if (stateKey) {
      if (application) {
        await putGetStartedStage(form);
      } else {
        await postApplication(stateKey, form);
      }
      return;
    }

    const newStateKey = await mutatePostState({
      body: getInitialStateBody(affiliateTag, fromCampaign, {
        email: form.email,
        marketingOptIn: form.marketingOptIn,
      } as TGetStartedDataDE),
    });
    if (!newStateKey) throw new Error('POST state returned no state_key');

    await postApplication(newStateKey, form);
  };

  return { setUpAccount };
};
